import { SellTicketModelType, sellTicketStatusInversed, sellTicketTypeInversed } from '@teinor/erp/types/company/order/sell/sellTicket'
import { useCallback, useMemo, useState } from 'react'
import { SwitchButton, TabItems } from '../../../../../../../../../baseComponents/Button/SwitchButton'
import { DisplayPriceGettingParams } from '../../../../../../../../../baseComponents/Displays/DisplayPrice'
import TwinTable from '../../../../../../../../../baseComponents/TwinTable'
import { TableComponentProps } from '../../../../../../../../../baseComponents/TwinTable/BaseScreenTable/types'
import { TableInstanceType } from '../../../../../../../../../baseComponents/TwinTable/types'
import { CustomRenderDate, CustomRenderDateHours, CustomRenderString } from '../../../../../../../../../baseComponents/TwinTable/VirtualTableListing/CustomRenders'
import { ColumnTableSchema } from '../../../../../../../../../baseComponents/TwinTable/VirtualTableListing/Subcomponents/types'
import { OnRowClick } from '../../../../../../../../../baseComponents/TwinTable/VirtualTableListing/types'
import TwinTrans from '../../../../../../../../../baseComponents/TwinTrans'
import CustomSelect from '../../../../../../../../../forms/CustomSelect'
import { emptyExtraLineFirstSortFunction } from '../../../../../../../../../forms/CustomSelect/functions'
import { InputWithLabelMargin } from '../../../../../../../../../forms/Input'
import { RenderSellTicketStatus } from '../../../../../../../../../specificComponents/inputTableLayouts/order/OrderSellTicketTPVLayout/components/RenderSellTicketStatus'
import { TPVOrderData } from '../../../../../../../../../specificComponents/inputTableLayouts/order/OrderSellTicketTPVLayout/types'
import { twinFetchPostJSON } from '../../../../../../../../../utils/globals/data'
import { addDictionaryExtraLine, dictionaryComplexFromJsonArr, TwinDictionary } from '../../../../../../../../../utils/globals/dictionary'
import { sortEmployeesByUser } from '../../../../../../../../../utils/globals/employees'
import useSellTicketStatusType from '../../../../../../../../../utils/hooks/sells/orders/useSellTicketStatus'
import { UseEditDeleteModalProps } from '../../../../../../../../../utils/hooks/useEditDeleteModal'
import useTwinTranslation from '../../../../../../../../../utils/hooks/useTwinTranslation'
import { getAllWorkingCenters, getEmployees } from '../../../../../../../../../utils/reducers/getters'
import { HandleSellTicketTy } from '../../types'

type TableSellTicketListingTPVProps = TableComponentProps & TableSellTicketListingTPVLogicProps & {
    onRowClick?: OnRowClick
    handleSelectTicket: HandleSellTicketTy
}

const TableSellTicketListingTPV: React.FC<TableSellTicketListingTPVProps> = ({ onRowClick, handleSelectTicket, setOpened, completeData }) => {
    const { t, tVars } = useTwinTranslation()
    const { ticketFilter, setTicketFilter, tableInstance, setTableInstance, searchedTicket, setSearchedTicket } = useTableSellTicketListingTPVLogic({completeData})
    const { sellTicketStatus } = useSellTicketStatusType()
    const memoEmployees = useMemo(() => addDictionaryExtraLine(JSON.parse(JSON.stringify(getEmployees() || {}))), [])
    const workingCenters = getAllWorkingCenters()

    const columns: ColumnTableSchema<SellTicketModelType> = [
        {
            id: 'full_assigned_number',
            dataKey: 'full_assigned_number',
            label: t('ticket', 'Ticket'),
            sortable: true,
            customRender: (parameterValue, allRowData) => <CustomRenderString value={parameterValue} className={allRowData?.assigned_number ? '' : 'text-red-BA'} />,
            searchableComponent: {
                component: 'SearchableComponentBasic',
                extraComponentData: { label: t('ticket', 'Ticket') }
            },
            searchKey: 'SellTicket.full_assigned_number'
        },
        {
            id: 'ReservedSellTicketId',
            dataKey: 'ReservedSellTicketId',
            label: t('reservedTicketShort', 'Reserva'),
            simpleLabel: t('reservedTicket', 'Ticket de reserva'),
            sortable: true,
            customRender: (parameterValue, allRowData) => <CustomRenderString value={allRowData?.ReservedSellTicket?.full_assigned_number || allRowData?.full_assigned_reserved_number} />,
            searchableComponent: {
                component: 'SearchableComponentBasic',
                extraComponentData: { label: t('ticket', 'Ticket') }
            },
            searchKey: 'ReservedSellTicket.full_assigned_number'
        },
        {
            id: 'CustomerId',
            dataKey: 'CustomerId',
            label: t('customer', 'Cliente'),
            searchKey: 'Customer.name',
            minWidth: 130,
            customRender: (_parameterValue, allRowData) => <CustomRenderString value={allRowData?.Customer?.fiscal_name || allRowData?.Customer?.name || ''} />,
            searchableComponent: {
                component: 'SearchableComponentBasic',
                extraComponentData: { label: t('customer', 'Cliente') }
            },
        },
        {
            id: 'status',
            dataKey: 'status',
            label: t('status', 'Estado'),
            sortable: true,
            minWidth: 130,
            customRender: (parameterValue) => <RenderSellTicketStatus value={parameterValue} />,
            searchableComponent: {
                component: 'SearchableComponentsCustomSelect',
                extraComponentData: { items: sellTicketStatus, label: t('statusTicket', 'Estado del ticket') }
            },
            searchKey: 'SellTicket.status'
        },
        {
            id: 'WorkingCenterId',
            dataKey: 'WorkingCenterId',
            searchKey: 'WorkingCenter.name',
            searchSingleKey: 'WorkingCenterId',
            label: t('soldBy', 'Vendido por'),
            customRender: (_parameterValue, allRowData) => <CustomRenderString value={allRowData?.WorkingCenter?.name || ''} />,
            searchableComponent: {
                component: 'SearchableComponentsCustomSelectSearchBar',
                extraComponentData: { items: workingCenters || {}, label: t('workingCenter', 'Centro de trabajo') }
            },
            minWidth: 120
        },
        {
            id: 'processed_date',
            dataKey: 'processed_date',
            label: t('createdDate', 'Fecha de creación'),
            customRender: (parameterValue, allRowData) => <CustomRenderDateHours value={parameterValue || allRowData.createdAt} />,
            sortable: true,
            minWidth: 165
        },
        {
            id: 'total_total',
            dataKey: 'total_total',
            label: t('total', 'Total'),
            sortable: true,
            minWidth: 100,
            customRender: (parameterValue, allRowData) => <DisplayPriceGettingParams value={parseFloat(parameterValue)} with_vat={allRowData.with_vat} CurrencyId={allRowData.CurrencyId} />,
        },
        {
            id: 'CreateEmployeeId',
            dataKey: 'CreateEmployeeId',
            label: t('madeBy', 'Realizado por'),
            hideColumnByDefault: true,
            searchKey: 'CreateEmployee.fullname_short',
            customRender: (_parameterValue, allRowData) => {
                const employeeName = allRowData?.CreateEmployee ? allRowData?.CreateEmployee?.fullname_short : allRowData?.ProcessEmployee?.fullname_short
                return <CustomRenderString value={employeeName || ''} />
            },
            searchableComponent: {
                component: 'SearchableComponentsCustomSelectSearchBar',
                extraComponentData: {
                    items: memoEmployees,
                    label: t('madeBy', 'Realizado por'),
                    sortItems: true,
                    sortFunction: sortEmployeesByUser
                }
            },
            searchSingleKey: 'CreateEmployee.id'
        },
        {
            id: 'maxRefundDate',
            dataKey: 'maxRefundDate',
            label: t('maxRefundDateShort', 'Fecha máx. dev.'),
            simpleLabel: t('maxRefundDate', 'Fecha máxima de devolución'),
            customRender: (parameterValue, allRowData) => <CustomRenderDate value={parameterValue || ''} />,
            sortable: true,
            minWidth: 165,
        },
        {
            id: 'total_payed',
            dataKey: 'total_payed',
            label: t('total_payed', 'Total pagado'),
            sortable: true,
            minWidth: 100,
            customRender: (parameterValue, allRowData) => <DisplayPriceGettingParams value={parseFloat(parameterValue)} with_vat={allRowData.with_vat} CurrencyId={allRowData.CurrencyId} />,
        },
        {
            id: 'total_to_pay',
            dataKey: 'total_to_pay',
            label: t('total_to_pay', 'Total a pagar'),
            sortable: true,
            minWidth: 100,
            customRender: (parameterValue, allRowData) => <DisplayPriceGettingParams value={parseFloat(parameterValue)} with_vat={allRowData.with_vat} CurrencyId={allRowData.CurrencyId} />,
        },
    ]
    return (
        <div className='flex flex-auto flex-col'>
            <SellTicketFilters {...{ sellTicketStatus, setTicketFilter, tableInstance, handleSelectTicket, setOpened, setSearchedTicket, ticketFilter }} />
            {ticketFilter ?
                ticketFilter?.['SellTicket.full_assigned_number']=== false ?
                    <div className='flex flex-auto items-center justify-center regular16'> {tVars('ticketNotFoundWithReference', 'No se ha encontrado ningún ticket con nº {{full_assigned_number}}', { full_assigned_number: searchedTicket })}</div>
                    : <TwinTable className={'sellTicketsTable mt-5'} name={'sellTickets'} columns={columns} getDataFrom={'/api/app/order/tpv/sellTicket/getAllSellTicketsComplete'} setTableInstance={setTableInstance} extraWhereSubstituibleParams={ticketFilter} onRowClick={onRowClick} />
            : null}
        </div>
    )
}

interface TicketFilterProps {
    ['SellTicket.status']?: any
    ['SellTicket.full_assigned_number']?: any
    ['SellTicket.WorkingCenterId']?: any
    ['SellTicket.WorkingCenterStationId']?: any
    ['SellTicket.type']?: any
}
interface TableSellTicketListingTPVLogicProps {
    completeData: TPVOrderData
}
const useTableSellTicketListingTPVLogic = ({completeData}: TableSellTicketListingTPVLogicProps) => {
    const [ticketFilter, setTicketFilter] = useState<TicketFilterProps | null>({
        'SellTicket.type': {'$notN': 1},
        'SellTicket.WorkingCenterId': completeData?.cashRegister?.WorkingCenterId,
        "SellTicket.WorkingCenterStationId": completeData?.cashRegister?.WorkingCenterStationId,
        "SellTicket.status": { "$gteN": 3 }
    })
    const [tableInstance, setTableInstance] = useState<TableInstanceType>(null)
    const [searchedTicket, setSearchedTicket] = useState<string>('')
    return { ticketFilter, setTicketFilter, tableInstance, setTableInstance, searchedTicket, setSearchedTicket }
}

interface SellTicketFiltersProps extends SellTicketFiltersLogicProps {
}

const SellTicketFilters: React.FC<SellTicketFiltersProps> = ({ ...rest }) => {
    const { t } = useTwinTranslation()
    const { handleSelectFilters, dictWorkingCenters, dictStatuses, dictWorkingCenterStations, searchSpecificTicket, ticketNumber, setTicketNumber, ticketType, handleTicketType } = useSellTicketFiltersLogic({ ...rest })
    const items: TabItems[] = [
        {
            element: <TwinTrans transKey={'withoutDeposits'}>Sin depósitos</TwinTrans>
        },
        {
            element: <TwinTrans transKey={'allM'}>Todos</TwinTrans>
        },
    ]
    return (
        <div className='flex gap-30 mb-20 items-end mt-5'>
            <div>
                <InputWithLabelMargin className='ticket_number_selector min-w-210 product_table_filters ' value={ticketNumber} label={t('ticketNumber', 'Nº de ticket')} onChange={setTicketNumber} onKeyDown={searchSpecificTicket} autoFocus />
            </div> 
            <CustomSelect label={t('status', 'Estado')} items={dictStatuses} className='min-w-210 product_table_filters ' name='status' onChange={(value) => handleSelectFilters(value, 'SellTicket.status')} sortFunction={emptyExtraLineFirstSortFunction} value={3}/>
            <CustomSelect label={t('workingCenter', 'Centro de trabajo')} items={dictWorkingCenters} className='min-w-210 product_table_filters ' name='WorkingCenterId' onChange={(value) => handleSelectFilters(value, 'SellTicket.WorkingCenterId')} sortFunction={emptyExtraLineFirstSortFunction} value={rest.ticketFilter?.['SellTicket.WorkingCenterId']}/>
            <CustomSelect label={t('workStation', 'Puesto')} items={dictWorkingCenterStations} className='min-w-210 product_table_filters ' name='WorkingCenterStationId' onChange={(value) => handleSelectFilters(value, 'SellTicket.WorkingCenterStationId')} sortFunction={emptyExtraLineFirstSortFunction} value={rest.ticketFilter?.['SellTicket.WorkingCenterStationId']} />
            <SwitchButton className='mr-15' onClick={(val) => handleTicketType(val)} active={ticketType} items={items} />
        </div>
    )
}

interface SellTicketFiltersLogicProps {
    setTicketFilter: React.Dispatch<React.SetStateAction<TicketFilterProps | null>>
    ticketFilter: TicketFilterProps | null
    sellTicketStatus: TwinDictionary
    handleSelectTicket: HandleSellTicketTy
    setOpened: React.Dispatch<UseEditDeleteModalProps>
    tableInstance: TableInstanceType
    setSearchedTicket: React.Dispatch<string>
}

const useSellTicketFiltersLogic = ({ setTicketFilter, ticketFilter, sellTicketStatus, handleSelectTicket, setOpened, tableInstance, setSearchedTicket }: SellTicketFiltersLogicProps) => {
    const workingCenters = getAllWorkingCenters()
    const dictWorkingCenters = useMemo(() => addDictionaryExtraLine(JSON.parse(JSON.stringify(workingCenters || {}))), [workingCenters])
    const dictStatuses = useMemo(() => addDictionaryExtraLine(JSON.parse(JSON.stringify(sellTicketStatus || {}))), [sellTicketStatus])
    const dictWorkingCenterStations = useMemo(() => addDictionaryExtraLine(JSON.parse(JSON.stringify(ticketFilter?.['SellTicket.WorkingCenterId'] ? dictionaryComplexFromJsonArr(dictWorkingCenters?.[ticketFilter?.['SellTicket.WorkingCenterId']]?.WorkingCenterStations || []) : {}))), [dictWorkingCenters, ticketFilter])
    const [ticketNumber, setTicketNumber] = useState<string>('')
    const [ticketType, setTicketType] = useState<number>(0)

    const handleSelectFilters = useCallback((value: any, fieldName: string) => {
        setTicketFilter((old) => {
            const copyOld = JSON.parse(JSON.stringify(old || {}))
            if (value) {
                let myValue = value
                if (fieldName === 'SellTicket.status' && parseInt(String(value)) === sellTicketStatusInversed['accepted']) {
                    myValue = { '$gteN': sellTicketStatusInversed['accepted']} 
                }
                copyOld[fieldName] = myValue
            } else {
                delete copyOld[fieldName]
            }
            if (Object.keys(copyOld).length && !copyOld['type']) {
                copyOld['SellTicket.type'] = { '$notN': sellTicketTypeInversed['partialCharged'] } 
            }
            delete copyOld['SellTicket.full_assigned_number']
            return Object.keys(copyOld).length ? copyOld : null
        })
        tableInstance?.getTableDataFromStart()
    }, [setTicketFilter, tableInstance])

    const handleTicketType = useCallback((val: number) => {
        setTicketType(val)
        setTicketFilter((old) => {
            const copyOld = JSON.parse(JSON.stringify(old || {}))
            if (Object.keys(copyOld).length) {
                if (val) {
                    copyOld['SellTicket.type'] = { '$gteN': sellTicketTypeInversed['sell'] }
                } else {
                    copyOld['SellTicket.type'] = { '$notN': sellTicketTypeInversed['partialCharged'] }
                }
            }
            delete copyOld['SellTicket.full_assigned_number']
            return Object.keys(copyOld).length ? copyOld : null
        })
        tableInstance?.getTableDataFromStart()
    }, [setTicketType, setTicketFilter, tableInstance])

    const searchSpecificTicket = useCallback(async (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'Enter') {
            let result = null
            if (ticketNumber) {
                result = await twinFetchPostJSON('/api/app/order/tpv/sellTicket/getSpecificSellTicket', {
                    full_assigned_number: ticketNumber.trim()
                })
            }
            if (result && result.id) {
                handleSelectTicket?.(result.id, result.full_assigned_number)
                setOpened(null)
            } else {
                setSearchedTicket(ticketNumber)
                setTicketFilter((old) => {
                    const copyOld = JSON.parse(JSON.stringify(old || {}))
                    copyOld['SellTicket.full_assigned_number'] = false
                    return copyOld
                })
            }
            setTicketNumber('')
            //@ts-ignore
            document.querySelector('.ticket_number_selector input')?.focus()
        }
    }, [ticketNumber, setTicketNumber, handleSelectTicket, setOpened, setTicketFilter, setSearchedTicket])

    return { handleSelectFilters, dictWorkingCenters, dictStatuses, dictWorkingCenterStations, ticketNumber, setTicketNumber, searchSpecificTicket, ticketType, handleTicketType }
}



export default TableSellTicketListingTPV
